// ASC-v3 RH-UI — Routing Examples editor, framed as an AGENT ROUTING PROPERTY
// (not a chat-"Pipeline" artifact), so it's meaningful for a voice supervisor
// that has no pipeline. Per-expert example questions are embedded and matched to
// route a caller to the right expert — the same routing on voice (warm handoff)
// and chat (L1.5). Saving persists to exemplars_json (the field the runtime reads)
// with regenerated embeddings, via agentApi.updateExemplars (RH-STORE).
//
// Mockup: docs/design/agent-routing-examples-mockup-v1.html.
import { useState } from "react";
import { agentApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import {
  sensitivityToConfig,
  configToSensitivity,
  clampRoutingConfig,
  sensitivityLabel,
  routingFromAgent,
} from "./routingSensitivity";

// Read the current example TEXTS from the field routing reads (exemplars_json),
// falling back to legacy fields only for pre-RH-STORE display.
export function exemplarTexts(a) {
  const ej = a?.exemplars_json;
  if (ej && Array.isArray(ej.exemplars)) {
    return ej.exemplars.map((x) => x?.text).filter(Boolean);
  }
  return a?.metadata_json?.exemplars || a?.flow_config?.exemplars || [];
}

const CYAN = COLORS.cyan;

function ExpertCard({ expert, canEdit, onSaved }) {
  const [draft, setDraft] = useState(() => exemplarTexts(expert));
  const [input, setInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const add = () => {
    const v = input.trim();
    if (!v) return;
    setDraft((d) => [...d, v]);
    setInput("");
    setDirty(true);
  };
  const remove = (i) => {
    setDraft((d) => d.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // draft is always clean — add() trims + guards empty, initial is
      // Boolean-filtered by exemplarTexts — so no filter needed here.
      await agentApi.updateExemplars(expert.id, draft);
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="expert-card" style={{
      border: `1px solid ${COLORS.border}`, borderRadius: 12,
      background: COLORS.surface, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em",
          color: CYAN, background: `${CYAN}22`, padding: "2px 7px", borderRadius: 999,
        }}>Expert</span>
        <b style={{ fontSize: 14, color: COLORS.text }}>{expert.name}</b>
        <span style={{ marginLeft: "auto", fontSize: 11, color: COLORS.textDim, fontFamily: "var(--mono, monospace)" }}>
          {draft.length} example{draft.length === 1 ? "" : "s"}
        </span>
      </div>

      {draft.length === 0 && (
        <div style={{ fontSize: 12, color: COLORS.textDim }}>
          No routing examples — this expert won&apos;t be reachable by fast routing until you add some.
        </div>
      )}
      {draft.map((q, i) => (
        <div key={i} data-testid="example-row" style={{
          display: "flex", alignItems: "center", gap: 8,
          background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "8px 10px",
        }}>
          <span style={{ flex: 1, fontSize: 12.5, color: COLORS.text }}>{q}</span>
          {canEdit && (
            <span data-testid="remove-example" onClick={() => remove(i)} role="button" tabIndex={0}
              style={{ color: COLORS.textDim, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 2px" }}>×</span>
          )}
        </div>
      ))}

      {canEdit && (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="add-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Add an example question a caller might ask…"
            style={{
              flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10,
              padding: "8px 11px", color: COLORS.text, fontSize: 12.5,
            }}
          />
          <button data-testid="add-btn" onClick={add} style={btn("ghost")}>Add</button>
        </div>
      )}

      {error && <div style={{ color: COLORS.pink, fontSize: 12 }}>{error}</div>}

      {canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {dirty && <span style={{ fontSize: 11, color: COLORS.orange }}>Unsaved · saving re-embeds (BYOK)</span>}
          <span style={{ flex: 1 }} />
          <button data-testid="save-btn" onClick={save} disabled={!dirty || saving} style={btn(dirty ? "primary" : "disabled")}>
            {saving ? "Saving…" : "Save & re-embed"}
          </button>
        </div>
      )}
    </div>
  );
}

function btn(kind) {
  const base = { border: "none", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" };
  if (kind === "primary") return { ...base, background: COLORS.accent, color: "#fff" };
  if (kind === "disabled") return { ...base, background: COLORS.surface, color: COLORS.textDim, border: `1px solid ${COLORS.border}`, cursor: "default" };
  return { ...base, background: "transparent", color: COLORS.text, border: `1px solid ${COLORS.border}` };
}

// Supervisor-level routing sensitivity + live preview. Writes the SAME
// flow_config.supervisor.routing {floor, margin} the runtime resolves; a friendly
// Strict↔Lenient slider up front, raw floor/margin under Advanced. The test box
// scores a phrase against the current experts with the (possibly-unsaved) config.
function SupervisorRoutingCard({ agent, canEdit }) {
  const [cfg, setCfg] = useState(() => routingFromAgent(agent));
  const [advanced, setAdvanced] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [utterance, setUtterance] = useState("");
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);

  const sensitivity = configToSensitivity(cfg);

  const applyCfg = (next) => { setCfg(clampRoutingConfig(next)); setDirty(true); };
  const onSlider = (e) => applyCfg(sensitivityToConfig(parseFloat(e.target.value)));
  const onRaw = (key) => (e) => applyCfg({ ...cfg, [key]: parseFloat(e.target.value) });

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await agentApi.updateRoutingConfig(agent.id, cfg);
      setDirty(false);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    const u = utterance.trim();
    if (!u) return;
    setTesting(true); setPreviewErr(null);
    try {
      const res = await agentApi.previewRouting(agent.id, { utterance: u, floor: cfg.floor, margin: cfg.margin });
      setPreview(res?.data || res);
    } catch (e) {
      setPreviewErr(e?.message || String(e));
      setPreview(null);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="routing-sensitivity-card" style={{
      border: `1px solid ${COLORS.border}`, borderRadius: 12,
      background: COLORS.surface, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>Routing sensitivity</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2, maxWidth: 620 }}>
          How eagerly a caller is routed to an expert. <b>Strict</b> only routes on a very close match
          (today&apos;s behaviour); <b>Lenient</b> routes more, keeping a margin so ambiguous questions still
          fall through to the lead.
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 11, color: COLORS.textDim, width: 44 }}>Strict</span>
        <input
          data-testid="sensitivity-slider" type="range" min={0} max={1} step={0.01}
          value={sensitivity} onChange={onSlider} disabled={!canEdit}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: 11, color: COLORS.textDim, width: 52, textAlign: "right" }}>Lenient</span>
        <span data-testid="sensitivity-label" style={{
          fontSize: 11, fontWeight: 700, color: CYAN, background: `${CYAN}22`,
          padding: "2px 8px", borderRadius: 999, minWidth: 62, textAlign: "center",
        }}>{sensitivityLabel(sensitivity)}</span>
      </div>

      <div>
        <span data-testid="advanced-toggle" role="button" tabIndex={0}
          onClick={() => setAdvanced((v) => !v)}
          style={{ fontSize: 11.5, color: COLORS.textMuted, cursor: "pointer", textDecoration: "underline" }}>
          {advanced ? "Hide advanced" : "Advanced (raw floor / margin)"}
        </span>
        {advanced && (
          <div data-testid="advanced-raw" style={{ display: "flex", gap: 14, marginTop: 8 }}>
            <label style={{ fontSize: 11.5, color: COLORS.textDim, display: "flex", flexDirection: "column", gap: 3 }}>
              Floor (min match)
              <input data-testid="raw-floor" type="number" min={0} max={1} step={0.01}
                value={cfg.floor} onChange={onRaw("floor")} disabled={!canEdit}
                style={rawInput()} />
            </label>
            <label style={{ fontSize: 11.5, color: COLORS.textDim, display: "flex", flexDirection: "column", gap: 3 }}>
              Margin (lead over runner-up)
              <input data-testid="raw-margin" type="number" min={0} max={1} step={0.01}
                value={cfg.margin} onChange={onRaw("margin")} disabled={!canEdit}
                style={rawInput()} />
            </label>
          </div>
        )}
      </div>

      {error && <div style={{ color: COLORS.pink, fontSize: 12 }}>{error}</div>}

      {canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {dirty && <span style={{ fontSize: 11, color: COLORS.orange }}>Unsaved routing sensitivity</span>}
          <span style={{ flex: 1 }} />
          <button data-testid="routing-save-btn" onClick={save} disabled={!dirty || saving}
            style={btn(dirty ? "primary" : "disabled")}>
            {saving ? "Saving…" : "Save sensitivity"}
          </button>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>Test an utterance</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input data-testid="preview-input" value={utterance}
            onChange={(e) => setUtterance(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runTest(); }}
            placeholder="Type what a caller might say — see who it routes to…"
            style={{ flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "8px 11px", color: COLORS.text, fontSize: 12.5 }} />
          <button data-testid="preview-btn" onClick={runTest} disabled={testing || !utterance.trim()}
            style={btn(utterance.trim() && !testing ? "primary" : "disabled")}>
            {testing ? "Testing…" : "Test"}
          </button>
        </div>
        {previewErr && <div style={{ color: COLORS.pink, fontSize: 12 }}>{previewErr}</div>}
        {preview && (
          <div data-testid="preview-result" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 12.5, color: COLORS.text }}>
              {preview.would_route
                ? <>Routes to <b data-testid="preview-picked" style={{ color: CYAN }}>{preview.picked_expert}</b></>
                : <span data-testid="preview-noroute" style={{ color: COLORS.textMuted }}>Falls through to the lead — no expert clears the bar</span>}
              <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.textDim, fontFamily: "var(--mono, monospace)" }}>
                floor {preview.floor} · margin {preview.margin}
              </span>
            </div>
            {(preview.experts || []).length === 0 ? (
              <div style={{ fontSize: 11.5, color: COLORS.textDim }}>No experts with examples to score against.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {preview.experts.map((row) => (
                  <div key={row.expert} data-testid="preview-score-row" style={{
                    display: "flex", alignItems: "center", gap: 8,
                    fontSize: 11.5, color: COLORS.textMuted,
                  }}>
                    <span style={{ flex: 1 }}>{row.expert}</span>
                    <span style={{ fontFamily: "var(--mono, monospace)", color: COLORS.text }}>{Number(row.score).toFixed(3)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function rawInput() {
  return { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 9px", color: COLORS.text, fontSize: 12.5, width: 120 };
}

export default function RoutingExamplesPanel({ agent, experts = [], canEdit = false, onSaved }) {
  return (
    <div data-testid="routing-examples-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Routing Examples</div>
        <div style={{ fontSize: 12.5, color: COLORS.textMuted, marginTop: 3, maxWidth: 640 }}>
          Teach this supervisor which questions go to which expert. Examples are embedded and matched to route
          a caller instantly — the same routing on <b>voice</b> (warm handoff) and <b>chat</b> (L1.5).
        </div>
      </div>

      {agent?.id && <SupervisorRoutingCard agent={agent} canEdit={canEdit} />}

      {experts.length === 0 ? (
        <div style={{ color: COLORS.textDim, fontSize: 13 }}>This supervisor has no experts to route to.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
          {experts.map((e) => (
            <ExpertCard key={e.id} expert={e} canEdit={canEdit} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
